import { bareAdAccountId } from "@/lib/ads/adAccounts";

export const AD_ACCOUNT_HEADER = "X-Ad-Account-Id";
export const AD_ACCOUNT_URL_PARAM = "account";
export const AD_ACCOUNT_NOT_CONNECTED_CODE = "AD_ACCOUNT_NOT_CONNECTED";
export const AD_ACCOUNT_STORAGE_PREFIX = "wn.ads.account.";

export interface AdAccountSelectionChange {
  workspaceId: string;
  previous: string | null;
  next: string | null;
  resetCaches: boolean;
}

type Listener = (change: AdAccountSelectionChange) => void;

const selections = new Map<string, string | null>();
const implicitSelections = new Set<string>();
const listeners = new Set<Listener>();
let urlBinding: { value: string; workspaceId: string } | null = null;

const cleanId = (value: unknown): string | null => {
  const id = bareAdAccountId(value);
  return /^\d+$/.test(id) ? id : null;
};

export const adAccountStorageKey = (workspaceId: string): string =>
  `${AD_ACCOUNT_STORAGE_PREFIX}${workspaceId}`;

export function readStoredAdAccount(workspaceId: string | null | undefined): string | null {
  if (!workspaceId) return null;
  try {
    return cleanId(localStorage.getItem(adAccountStorageKey(workspaceId)));
  } catch {
    return null;
  }
}

function writeStoredAdAccount(workspaceId: string, id: string): void {
  try {
    localStorage.setItem(adAccountStorageKey(workspaceId), id);
  } catch {
    return;
  }
}

function clearStoredAdAccount(workspaceId: string, onlyIf?: string | null): void {
  try {
    const key = adAccountStorageKey(workspaceId);
    if (onlyIf) {
      const stored = cleanId(localStorage.getItem(key));
      if (stored && stored !== onlyIf) return;
    }
    localStorage.removeItem(key);
  } catch {
    return;
  }
}

const AD_ACCOUNT_URL_ROUTES = ["/home/ads", "/home/integrations/meta-ads"];

export function isAdAccountUrlRoute(pathname: string | null | undefined): boolean {
  const path = String(pathname ?? "").replace(/\/+$/, "");
  return AD_ACCOUNT_URL_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
}

export function readUrlAdAccount(): string | null {
  try {
    if (typeof window === "undefined") return null;
    if (!isAdAccountUrlRoute(window.location.pathname)) return null;
    return cleanId(new URLSearchParams(window.location.search).get(AD_ACCOUNT_URL_PARAM));
  } catch {
    return null;
  }
}

function replaceUrlAdAccount(workspaceId: string, id: string | null): void {
  try {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (!isAdAccountUrlRoute(url.pathname)) return;
    if (!url.searchParams.has(AD_ACCOUNT_URL_PARAM)) return;
    if (id) url.searchParams.set(AD_ACCOUNT_URL_PARAM, id);
    else url.searchParams.delete(AD_ACCOUNT_URL_PARAM);
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    urlBinding = id ? { value: id, workspaceId } : null;
  } catch {
    return;
  }
}

function currentWorkspaceId(): string | null {
  try {
    const stored = localStorage.getItem("selectedWorkspaceId");
    return stored && stored.trim() ? stored : null;
  } catch {
    return null;
  }
}

function claimUrlAdAccount(workspaceId: string): { value: string | null; fresh: boolean } {
  const fromUrl = readUrlAdAccount();
  if (!fromUrl) return { value: null, fresh: false };
  if (urlBinding && urlBinding.value === fromUrl) {
    return { value: urlBinding.workspaceId === workspaceId ? fromUrl : null, fresh: false };
  }
  const current = currentWorkspaceId();
  if (current && current !== workspaceId) return { value: null, fresh: false };
  urlBinding = { value: fromUrl, workspaceId };
  return { value: fromUrl, fresh: true };
}

function ensureInitialised(workspaceId: string): void {
  if (selections.has(workspaceId)) return;
  const fromUrl = claimUrlAdAccount(workspaceId).value;
  selections.set(workspaceId, fromUrl ?? readStoredAdAccount(workspaceId));
}

export function getSelectedAdAccountId(workspaceId: string | null | undefined): string | null {
  if (!workspaceId) return null;
  ensureInitialised(workspaceId);
  return selections.get(workspaceId) ?? null;
}

function emit(change: AdAccountSelectionChange): void {
  for (const listener of Array.from(listeners)) {
    try {
      listener(change);
    } catch {
      continue;
    }
  }
}

export function selectAdAccount(
  workspaceId: string | null | undefined,
  adAccountId: unknown,
  opts: { persist?: boolean; resetCaches?: boolean } = {},
): boolean {
  const id = cleanId(adAccountId);
  if (!workspaceId || !id) return false;
  const previous = getSelectedAdAccountId(workspaceId);
  if (opts.persist !== false) {
    writeStoredAdAccount(workspaceId, id);
    replaceUrlAdAccount(workspaceId, id);
    implicitSelections.delete(workspaceId);
  } else if (previous !== id) {
    implicitSelections.add(workspaceId);
  }
  if (previous === id) return false;
  selections.set(workspaceId, id);
  emit({ workspaceId, previous, next: id, resetCaches: opts.resetCaches !== false });
  return true;
}

export function clearAdAccountSelection(
  workspaceId: string | null | undefined,
  opts: { onlyIf?: unknown } = {},
): boolean {
  if (!workspaceId) return false;
  const previous = getSelectedAdAccountId(workspaceId);
  const onlyIf = opts.onlyIf === undefined ? null : cleanId(opts.onlyIf);
  if (opts.onlyIf !== undefined && previous !== onlyIf) return false;
  clearStoredAdAccount(workspaceId, previous);
  if (readUrlAdAccount() === previous) replaceUrlAdAccount(workspaceId, null);
  implicitSelections.delete(workspaceId);
  if (previous === null) return false;
  selections.set(workspaceId, null);
  emit({ workspaceId, previous, next: null, resetCaches: true });
  return true;
}

export function handleAdAccountNotConnected(
  workspaceId: string | null | undefined,
  sentAdAccountId: unknown,
): boolean {
  return clearAdAccountSelection(workspaceId, { onlyIf: sentAdAccountId });
}

export function rejectedSelectedAdAccountId(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const p = payload as { selected_ad_account_id?: unknown; selectedAdAccountId?: unknown; error?: unknown };
  const nested =
    p.error && typeof p.error === "object" && !Array.isArray(p.error)
      ? (p.error as { selected_ad_account_id?: unknown }).selected_ad_account_id
      : undefined;
  return cleanId(p.selected_ad_account_id ?? p.selectedAdAccountId ?? nested);
}

export function isSelectionRejection(payload: unknown, sentAdAccountId: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as { code?: unknown; errorCode?: unknown; error?: unknown };
  const nestedCode =
    p.error && typeof p.error === "object" && !Array.isArray(p.error)
      ? (p.error as { code?: unknown }).code
      : undefined;
  const code = p.code || p.errorCode || nestedCode;
  if (code !== AD_ACCOUNT_NOT_CONNECTED_CODE) return false;
  const sent = cleanId(sentAdAccountId);
  return !!sent && rejectedSelectedAdAccountId(payload) === sent;
}

export function applyUrlAdAccount(
  workspaceId: string | null | undefined,
  connectedIds: string[],
): boolean {
  if (!workspaceId) return false;
  const claim = claimUrlAdAccount(workspaceId);
  if (!claim.value || !claim.fresh) return false;
  if (!connectedIds.includes(claim.value)) return false;
  return selectAdAccount(workspaceId, claim.value);
}

export function isImplicitAdAccountSelection(workspaceId: string | null | undefined): boolean {
  return !!workspaceId && implicitSelections.has(workspaceId);
}

export interface SelectionValidationInput {
  accounts: Array<{ ad_account_id?: string | null; status?: string | null }> | null | undefined;
  account?: { ad_account_id?: string | null } | null;
  default_ad_account_id?: string | null;
  connected?: boolean;
}

export function connectedAdAccountIds(
  accounts: SelectionValidationInput["accounts"],
): string[] {
  return (Array.isArray(accounts) ? accounts : [])
    .filter((row) => String(row?.status ?? "").trim().toLowerCase() !== "disconnected")
    .map((row) => cleanId(row?.ad_account_id))
    .filter((id): id is string => !!id);
}

export function reconcileAdAccountSelection(
  workspaceId: string | null | undefined,
  status: SelectionValidationInput | null | undefined,
): string | null {
  if (!workspaceId || !status) return getSelectedAdAccountId(workspaceId);
  const ids = connectedAdAccountIds(status.accounts);
  const listed = Array.isArray(status.accounts) && (status.accounts.length > 0 || status.connected === false);
  applyUrlAdAccount(workspaceId, ids);
  const current = getSelectedAdAccountId(workspaceId);
  if (current && listed && !ids.includes(current)) {
    clearAdAccountSelection(workspaceId, { onlyIf: current });
    return null;
  }
  if (current && implicitSelections.has(workspaceId)) {
    const nextDefault = cleanId(status.default_ad_account_id);
    if (nextDefault && nextDefault !== current && (!listed || ids.includes(nextDefault))) {
      selectAdAccount(workspaceId, nextDefault, { persist: false, resetCaches: true });
      return nextDefault;
    }
  }
  if (!current) {
    const live = cleanId(status.account?.ad_account_id) ?? cleanId(status.default_ad_account_id);
    if (live && (!listed || ids.includes(live))) {
      selectAdAccount(workspaceId, live, { persist: false, resetCaches: false });
      return live;
    }
  }
  return current;
}

export function statusForSelection<T>(data: T, selectedId: string | null | undefined): T {
  const status = data as unknown as
    | {
        account?: Record<string, unknown> | null;
        accounts?: Array<Record<string, unknown>> | null;
      }
    | null
    | undefined;
  const id = cleanId(selectedId);
  if (!status || !id || !status.account) return data;
  if (cleanId(status.account.ad_account_id) === id) return data;
  const row = (Array.isArray(status.accounts) ? status.accounts : []).find(
    (r) =>
      cleanId(r?.ad_account_id) === id &&
      String(r?.status ?? "").trim().toLowerCase() !== "disconnected",
  );
  if (!row) return data;
  const account: Record<string, unknown> = {};
  for (const key of [
    "id",
    "ad_account_id",
    "ad_account_name",
    "page_id",
    "page_name",
    "currency",
    "status",
    "balance",
    "balance_last_synced",
    "has_dataset",
    "dataset_id",
    "token_status",
    "is_default",
  ]) {
    if (key in row) account[key] = row[key];
  }
  return { ...(status as object), account } as unknown as T;
}

export function subscribeAdAccountSelection(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function adAccountHeaderFor(
  endpoint: string,
  body?: unknown,
): { workspaceId: string | null; adAccountId: string | null } {
  const path = endpoint.split("?")[0];
  if (path !== "/ads" && !path.startsWith("/ads/")) return { workspaceId: null, adAccountId: null };
  let workspaceId: string | null = null;
  const q = endpoint.indexOf("?");
  if (q !== -1) {
    try {
      workspaceId = new URLSearchParams(endpoint.slice(q + 1)).get("workspace_id") || null;
    } catch {
      workspaceId = null;
    }
  }
  if (!workspaceId && body && typeof body === "object" && !Array.isArray(body)) {
    const fromBody = (body as { workspace_id?: unknown }).workspace_id;
    if (typeof fromBody === "string" && fromBody.trim()) workspaceId = fromBody;
  }
  if (!workspaceId) {
    try {
      const stored = localStorage.getItem("selectedWorkspaceId");
      workspaceId = stored && stored.trim() ? stored : null;
    } catch {
      workspaceId = null;
    }
  }
  return { workspaceId, adAccountId: getSelectedAdAccountId(workspaceId) };
}

export function resetAdAccountSelectionForTests(): void {
  selections.clear();
  implicitSelections.clear();
  urlBinding = null;
}
